import React, { useState, useEffect } from "react";
import Modal from "../../../../components/ui/Modal";
import Button from "../../../../components/ui/Button";
import Input from "../../../../components/ui/Input";
import Select from "../../../../components/ui/Select";
import { v4 as uuidv4 } from "uuid";
import { PlusCircle, Trash2 } from "lucide-react";
import { platformAdOpportunities } from "../../../../data/mockData"; // ✨ Import the new data

const adTypeConfig = {
  "Image Banner": [
    "Prime Ad",
    "In-Feed Ad",
    "Commerce Banner",
    "Interstitial Ad",
  ],
  "Video Ad": ["In-Feed Ad", "Interstitial Ad"],
  "Text Link Ad": ["In-Feed Ad"],
};
const adTypes = Object.keys(adTypeConfig);

const CreateCampaignModal = ({ isOpen, onClose, onSave, campaignToEdit }) => {
  const [source, setSource] = useState("Direct Deal"); // ✨ New state for source
  const [name, setName] = useState("");
  const [advertiser, setAdvertiser] = useState("");
  const [creatives, setCreatives] = useState([]);

  useEffect(() => {
    if (campaignToEdit) {
      setSource(campaignToEdit.source);
      setName(campaignToEdit.name);
      setAdvertiser(campaignToEdit.advertiser);
      setCreatives(
        campaignToEdit.creatives || [
          {
            id: uuidv4(),
            adType: "Image Banner",
            adZone: "Prime Ad",
            mediaUrl: "",
            link: "",
          },
        ]
      );
    } else {
      // Reset to default
      setSource("Direct Deal");
      setName("");
      setAdvertiser("");
      setCreatives([
        {
          id: uuidv4(),
          adType: "Image Banner",
          adZone: "Prime Ad",
          mediaUrl: "",
          link: "",
          impressions: 0,
          ctr: "0.0%",
          revenueETB: 0,
        },
      ]);
    }
  }, [campaignToEdit, isOpen]);

  const handlePlatformDealSelect = (campaignName) => {
    if (!campaignName) {
      setName("");
      setAdvertiser("");
      return;
    }
    const selectedDeal = platformAdOpportunities.find(
      (p) => p.campaignName === campaignName
    );
    if (selectedDeal) {
      setName(selectedDeal.campaignName);
      setAdvertiser(selectedDeal.advertiser);
    }
  };

  // --- Handlers for managing the creatives array ---
  const handleCreativeChange = (id, field, value) => {
    setCreatives(
      creatives.map((c) => {
        if (c.id === id) {
          const updatedCreative = { ...c, [field]: value };
          // If adType changes, reset adZone to the first compatible option
          if (field === "adType") {
            updatedCreative.adZone = adTypeConfig[value][0];
          }
          return updatedCreative;
        }
        return c;
      })
    );
  };

  const addCreative = () => {
    setCreatives([
      ...creatives,
      {
        id: uuidv4(),
        adType: "Image Banner",
        adZone: "Prime Ad",
        mediaUrl: "",
        link: "",
      },
    ]);
  };

  const removeCreative = (id) => {
    if (creatives.length > 1) {
      // Prevent removing the last creative
      setCreatives(creatives.filter((c) => c.id !== id));
    }
  };

  const handleSave = () => {
    // Basic validation
    if (
      !name ||
      !advertiser ||
      creatives.some(
        (c) => !c.adZone || (!c.mediaUrl && !c.headline) || !c.link
      )
    ) {
      alert("Please fill out all campaign and creative details.");
      return;
    }

    const campaignData = {
      id: campaignToEdit ? campaignToEdit.id : `CAMP-${uuidv4().slice(0, 4)}`,
      name,
      advertiser,
      status: campaignToEdit ? campaignToEdit.status : "Active", // Default new campaigns to Active
      source: campaignToEdit ? campaignToEdit.source : "Creator-Sourced",
      creatives,
    };
    onSave(campaignData);
    onClose();
  };

  const title = campaignToEdit ? "Edit Ad Campaign" : "Create New Ad Campaign";

  return (
    <Modal title={title} isOpen={isOpen} onClose={onClose} size="2xl">
      <div className="space-y-4">
        {/* --- ✨ NEW: Source Selection --- */}
        <div>
          <label className="block text-sm font-medium text-slate-700">
            Campaign Source
          </label>
          <div className="mt-1 grid grid-cols-2 gap-2 rounded-md bg-slate-100 p-1">
            <button
              onClick={() => setSource("Direct Deal")}
              className={`px-3 py-1.5 text-sm font-medium rounded ${
                source === "Direct Deal"
                  ? "bg-white shadow text-primary"
                  : "text-slate-600"
              }`}
            >
              Direct Deal
            </button>
            <button
              onClick={() => setSource("Platform-Sourced")}
              className={`px-3 py-1.5 text-sm font-medium rounded ${
                source === "Platform-Sourced"
                  ? "bg-white shadow text-primary"
                  : "text-slate-600"
              }`}
            >
              Platform-Sourced
            </button>
          </div>
        </div>

        {source === "Platform-Sourced" ? (
          <div>
            <label className="block text-sm font-medium text-slate-700">
              Available Platform Deals
            </label>
            <select
              onChange={(e) => handlePlatformDealSelect(e.target.value)}
              className="mt-1 block w-full rounded-md border-slate-300 shadow-sm"
            >
              <option value="">Select a deal...</option>
              {platformAdOpportunities.map((p) => (
                <option key={p.id} value={p.campaignName}>
                  {p.campaignName}
                </option>
              ))}
            </select>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Campaign Name
                </label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Zemen Bank Q4"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Advertiser
                </label>
                <Input
                  value={advertiser}
                  onChange={(e) => setAdvertiser(e.target.value)}
                  placeholder="e.g., Zemen Bank"
                  className="mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Budget (ETB)
                </label>
                <Input
                  type="number"
                  placeholder="0.00"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Duration (Days)
                </label>
                <Input
                  type="number"
                  placeholder="30"
                  className="mt-1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700">
                  Rev. Share (%)
                </label>
                <Input
                  type="number"
                  placeholder="100"
                  className="mt-1"
                />
              </div>
            </div>
          </div>
        )}

        <hr className="my-4" />

        {/* --- Step 2: Ad Creative Builder --- */}
        <h3 className="text-md font-semibold text-slate-800">Ad Creatives</h3>
        <div className="space-y-4">
          {creatives.map((creative, index) => (
            <div
              key={creative.id}
              className="p-4 border border-slate-200 rounded-lg space-y-3 bg-slate-50 relative"
            >
              {creatives.length > 1 && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 h-7 w-7"
                  onClick={() => removeCreative(creative.id)}
                >
                  <Trash2 size={16} className="text-red-500" />
                </Button>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-600">
                    Ad Type
                  </label>
                  <select
                    value={creative.adType}
                    onChange={(e) =>
                      handleCreativeChange(
                        creative.id,
                        "adType",
                        e.target.value
                      )
                    }
                    className="mt-1 block w-full rounded-md border-slate-300 shadow-sm text-sm"
                  >
                    {adTypes.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-600">
                    Ad Zone
                  </label>
                  <select
                    value={creative.adZone}
                    onChange={(e) =>
                      handleCreativeChange(
                        creative.id,
                        "adZone",
                        e.target.value
                      )
                    }
                    className="mt-1 block w-full rounded-md border-slate-300 shadow-sm text-sm"
                  >
                    {adTypeConfig[creative.adType].map((zone) => (
                      <option key={zone} value={zone}>
                        {zone}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">
                  {creative.adType === "Text Link Ad"
                    ? "Headline"
                    : "Media URL"}
                </label>
                <input
                  type="text"
                  value={creative.mediaUrl || creative.headline || ""}
                  onChange={(e) =>
                    handleCreativeChange(
                      creative.id,
                      creative.adType === "Text Link Ad"
                        ? "headline"
                        : "mediaUrl",
                      e.target.value
                    )
                  }
                  placeholder={
                    creative.adType === "Text Link Ad"
                      ? "Your ad text here..."
                      : "https://..."
                  }
                  className="mt-1 block w-full rounded-md border-slate-300 shadow-sm text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600">
                  Call-to-Action Link
                </label>
                <input
                  type="url"
                  value={creative.link}
                  onChange={(e) =>
                    handleCreativeChange(creative.id, "link", e.target.value)
                  }
                  placeholder="https://..."
                  className="mt-1 block w-full rounded-md border-slate-300 shadow-sm text-sm"
                />
              </div>
            </div>
          ))}
        </div>
        <Button variant="secondary" size="sm" onClick={addCreative}>
          <PlusCircle size={16} className="mr-2" /> Add Another Creative
        </Button>
      </div>

      <div className="mt-6 flex justify-end space-x-2">
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={handleSave}>
          Save Campaign
        </Button>
      </div>
    </Modal>
  );
};

export default CreateCampaignModal;
